import { useState, useCallback, useRef } from 'react';

/**
 * Custom hook to manage button submission state and prevent double clicks
 * This hook ensures that only one submission can be in progress at a time
 * and automatically re-enables the button after errors or completion
 */
export const useButtonState = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submitTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    /**
     * Start submission state - disables the button
     */
    const startSubmission = useCallback(() => {
        setIsSubmitting(true);

        // Clear any existing timeout
        if (submitTimeoutRef.current) {
            clearTimeout(submitTimeoutRef.current);
        }

        // Safety timeout - re-enable after 30 seconds to prevent permanent lock
        submitTimeoutRef.current = setTimeout(() => {
            setIsSubmitting(false);
        }, 30000);
    }, []);

    /**
     * End submission state - re-enables the button
     */
    const endSubmission = useCallback(() => {
        setIsSubmitting(false);

        // Clear the safety timeout
        if (submitTimeoutRef.current) {
            clearTimeout(submitTimeoutRef.current);
            submitTimeoutRef.current = null;
        }
    }, []);

    /**
     * Wrapper function for async operations that automatically handles submission state
     * @param asyncOperation - The async function to execute
     * @returns Promise that resolves when the operation completes
     */
    const executeWithLoading = useCallback(async <T>(
        asyncOperation: () => Promise<T>
    ): Promise<T> => {
        if (isSubmitting) {
            // Prevent multiple simultaneous submissions
            return Promise.reject(new Error('Already submitting'));
        }

        startSubmission();

        try {
            const result = await asyncOperation();
            endSubmission();
            return result;
        } catch (error) {
            endSubmission();
            throw error;
        }
    }, [isSubmitting, startSubmission, endSubmission]);

    // Cleanup timeout on unmount
    const cleanup = useCallback(() => {
        if (submitTimeoutRef.current) {
            clearTimeout(submitTimeoutRef.current);
            submitTimeoutRef.current = null;
        }
    }, []);

    return {
        isSubmitting,
        startSubmission,
        endSubmission,
        executeWithLoading,
        cleanup,
    };
};
